<script lang="ts">
  import { gsap } from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  import type { Snippet } from 'svelte';

  gsap.registerPlugin(ScrollTrigger);

  interface Props {
    children: Snippet;
    className?: string;
    direction?: 'up' | 'down' | 'left' | 'right' | 'scale';
    stagger?: number;
    delay?: number;
    duration?: number;
    y?: number;
    x?: number;
    start?: string;
  }

  let {
    children,
    className = '',
    direction = 'up',
    stagger = 0.1,
    delay = 0,
    duration = 0.7,
    y = 40,
    x = 40,
    start = 'top 80%',
  }: Props = $props();

  let ref: HTMLDivElement | undefined;

  $effect(() => {
    if (!ref) return;

    const ctx = gsap.context(() => {
      const fromVars: gsap.TweenVars = { opacity: 0 };
      const toVars: gsap.TweenVars = {
        opacity: 1,
        duration,
        delay,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ref,
          start,
          toggleActions: 'play none none none',
        },
      };

      switch (direction) {
        case 'up':
          fromVars.y = y;
          toVars.y = 0;
          break;
        case 'down':
          fromVars.y = -y;
          toVars.y = 0;
          break;
        case 'left':
          fromVars.x = -x;
          toVars.x = 0;
          break;
        case 'right':
          fromVars.x = x;
          toVars.x = 0;
          break;
        case 'scale':
          fromVars.scale = 0.97;
          toVars.scale = 1;
          break;
      }

      const childElements = ref!.children;
      if (childElements.length > 1 && stagger > 0) {
        gsap.fromTo(Array.from(childElements), fromVars, { ...toVars, stagger });
      } else {
        gsap.fromTo(ref!, fromVars, toVars);
      }
    }, ref);

    return () => ctx.revert();
  });
</script>

<div bind:this={ref} class={className}>
  {@render children()}
</div>
